import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useMobile } from '../../../hooks/useMobile';
import { palette, typography, theme as customTheme } from '../../../theme/colors';
import type { SwipeableStatCardProps } from './types';

/**
 * SwipeableStatCard Component
 *
 * Mobile-optimized swipeable carousel for dashboard stat cards.
 * - Shows 1.2 cards on small mobile (peek next card)
 * - Shows 2.2 cards on large mobile
 * - Grid layout on tablet/desktop
 * - CSS scroll-snap for smooth native scrolling
 * - Pagination dots for navigation
 */
export const SwipeableStatCard: React.FC<SwipeableStatCardProps> = ({ cards }) => {
  const { isMobile, breakpoint } = useMobile();
  const theme = useTheme();
  const [activeCard, setActiveCard] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get theme-aware colors
  const colors = {
    surface: theme.palette.background.paper,
    border: theme.palette.divider,
    textPrimary: theme.palette.text.primary,
    textMuted: theme.palette.text.disabled,
  };

  /**
   * Helper function to get trend icon based on direction
   * Returns undefined instead of null for Material-UI type compatibility
   */
  const getTrendIcon = (direction: 'up' | 'down' | 'neutral'): React.ReactElement | undefined => {
    if (direction === 'up') {
      return <ArrowUpwardIcon sx={{ fontSize: 14 }} />;
    }
    if (direction === 'down') {
      return <ArrowDownwardIcon sx={{ fontSize: 14 }} />;
    }
    return undefined; // Material-UI Chip expects undefined, not null
  };

  // Handle scroll to update active pagination dot
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isMobile) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.firstElementChild?.clientWidth || 0;
      const currentIndex = Math.round(scrollLeft / cardWidth);
      setActiveCard(currentIndex);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Determine card width based on breakpoint
  const getCardWidth = () => {
    if (!isMobile) return 'auto';
    if (breakpoint === 'xs') return 'calc(83.33% - 8px)'; // 1.2 cards visible
    if (breakpoint === 'sm') return 'calc(45.45% - 8px)'; // 2.2 cards visible
    return 'calc(83.33% - 8px)';
  };

  const cardWidth = getCardWidth();

  return (
    <Box>
      {/* Swipeable/Grid Container */}
      <Box
        ref={containerRef}
        data-testid="swipeable-stat-cards-container"
        aria-label="Statistiques du tableau de bord"
        sx={{
          display: isMobile ? 'flex' : 'grid',
          gridTemplateColumns: isMobile ? 'none' : 'repeat(3, 1fr)',
          gap: 2,
          mb: isMobile ? 2 : 3,
          overflowX: isMobile ? 'auto' : 'visible',
          scrollSnapType: isMobile ? 'x mandatory' : 'none',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
          '&::-webkit-scrollbar': {
            display: 'none', // Hide scrollbar but keep functionality
          },
          scrollbarWidth: 'none', // Firefox
        }}
      >
        {cards.map((card, index) => (
          <Box
            key={card.id}
            data-testid={`stat-card-${card.id}`}
            role="region"
            aria-label={card.title}
            sx={{
              minWidth: isMobile ? cardWidth : 'auto',
              maxWidth: isMobile ? cardWidth : 'none',
              flexShrink: 0,
              scrollSnapAlign: isMobile ? 'start' : 'none',
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: customTheme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              cursor: 'pointer',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: card.color,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(card.color, 0.2)}`,
                borderColor: card.color,
              },
            }}
          >
            {/* Header with Trend */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              {/* Icon placeholder - icons would be rendered here in real implementation */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '4px',
                  backgroundColor: alpha(card.color, 0.1),
                }}
              />

              {/* Trend Chip */}
              {card.trend && (
                <Chip
                  icon={getTrendIcon(card.trend.direction)}
                  label={card.trend.value}
                  size="small"
                  sx={{
                    backgroundColor: alpha(
                      card.trend.direction === 'up' ? palette.success : palette.error,
                      0.1
                    ),
                    color: card.trend.direction === 'up' ? palette.success : palette.error,
                    fontWeight: typography.fontWeight.bold,
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.mono,
                    border: `1px solid ${
                      card.trend.direction === 'up' ? palette.success : palette.error
                    }`,
                    height: 24,
                    '& .MuiChip-icon': {
                      color: card.trend.direction === 'up' ? palette.success : palette.error,
                    },
                  }}
                />
              )}
            </Box>

            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {card.title}
            </Typography>

            {/* Value */}
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                lineHeight: 1,
                my: 0.5,
              }}
            >
              {card.value}
            </Typography>

            {/* Sub Value */}
            {card.subValue && (
              <Typography
                variant="body2"
                sx={{
                  color: card.color,
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  fontSize: typography.fontSize.sm,
                }}
              >
                {card.subValue}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Pagination Dots (Mobile Only) */}
      {isMobile && cards.length > 0 && (
        <Box
          data-testid="pagination-dots"
          role="group"
          aria-label="Pagination des cartes"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          {cards.map((card, index) => (
            <Box
              key={`dot-${card.id}`}
              data-testid={`pagination-dot-${index}`}
              aria-label={`Aller à la carte ${index + 1}`}
              aria-current={activeCard === index ? 'true' : 'false'}
              onClick={() => {
                if (containerRef.current) {
                  const cardWidth = containerRef.current.firstElementChild?.clientWidth || 0;
                  containerRef.current.scrollTo({
                    left: cardWidth * index,
                    behavior: 'smooth',
                  });
                }
              }}
              sx={{
                width: activeCard === index ? 24 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: activeCard === index ? palette.primary : alpha(colors.border, 0.5),
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: palette.primary,
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
